import { Component, Inject, OnInit } from '@angular/core';
import { NavController } from '@ionic/angular';
import {
  BehaviorSubject,
  Observable,
  Subject,
  delay,
  filter,
  map,
  of,
  skipWhile,
  switchMap,
  take,
} from 'rxjs';
import { valueChangedFadeInOut } from 'src/app/animations/fade-in-out.animation';
import { DailyMenu, WeekDay, WeeklyMenu } from 'src/app/api/contracts/menu';
import { OrderType } from 'src/app/api/contracts/order';
import { InStoreProduct, Product } from 'src/app/api/contracts/product';
import { APP_CONFIG, AppConfig } from 'src/app/config/app-config';
import { ClientService } from 'src/app/services/client.service';
import { InStoreProductsService } from 'src/app/services/in-store-products.service';
import { MealMenuService } from 'src/app/services/meal-menu.service';
import { ModalService } from 'src/app/services/modal.service';
import { StorageKeys, StorageService } from 'src/app/services/storage.service';
import { WeekIndex } from 'src/app/types/menu';
import {
  getCurrentWeekNumber,
  getNextWeekNumber,
  getWeekFirstDay,
} from 'src/app/utils/date.utils';

@Component({
  selector: 'app-home',
  templateUrl: 'home.page.html',
  styleUrls: ['home.page.scss'],
  animations: [valueChangedFadeInOut()],
})
export class HomePage implements OnInit {
  public readonly newProducts: Observable<InStoreProduct[]>;
  public readonly selectedDay: BehaviorSubject<WeekDay>;
  public readonly scrollTrigger$ = new Subject<void>();

  public dailyMenu$: Observable<DailyMenu | null> = of(null);
  public selectedWeeklyMenu$: Observable<WeeklyMenu | null> = of(null);
  public displayedDays$: Observable<WeekDay[]> = of([]);
  public selectedWeekIndex: number = 0;
  public currentWeekIsNotAvailable = false;

  constructor(
    private readonly navCtrl: NavController,
    public readonly menuService: MealMenuService,
    private readonly inStoreProductsService: InStoreProductsService,
    private readonly clientService: ClientService,
    private readonly modalService: ModalService,
    private readonly storageService: StorageService,
    @Inject(APP_CONFIG) private readonly appConfig: AppConfig
  ) {
    this.selectedDay = new BehaviorSubject<WeekDay>(getWeekFirstDay());
    this.newProducts = this.inStoreProductsService.newProducts$
      .asObservable()
      .pipe(filter((x) => x !== null)) as Observable<InStoreProduct[]>;
    this.selectedWeeklyMenu$ = this.menuService.selectedWeeklyMenu$;
  }

  ngOnInit() {
    this.menuService.retrieveWeeklyMenus().subscribe();
    this.initWeeklyMenu();
  }

  ionViewWillEnter() {
    this.handleNoCreditCardAddedPopup()
      .pipe(
        switchMap((userResponseFromModal: true | undefined | null) => {
          if (
            userResponseFromModal === true || // user agreed
            userResponseFromModal === undefined // user dismissed
          ) {
            this.storageService
              .set(StorageKeys.NO_CREDIT_CARD_ADDED_SHOWN, true)
              .subscribe();
          }
          if (userResponseFromModal === true) {
            return this.clientService
              .openCardEditingDashboard()
              .pipe(switchMap(() => this.clientService.retrieveClientCard()));
          }
          return of(null);
        })
      )
      .subscribe(() => {
        this.handleNewProductsPopupPresenting();
      });
  }

  openProductDetails(product: Product): void {
    const selectedMenu = this.menuService.selectedWeeklyMenu$.getValue();
    this.navCtrl.navigateForward(`store/product/${product.id}`, {
      queryParams: {
        type: OrderType.DELIVERY,
        day: this.selectedDay.value,
        weeklyMenuId: selectedMenu!.id,
        weekNumber: selectedMenu!.weekNumber,
      },
    });
  }

  private handleNoCreditCardAddedPopup(): Observable<any> {
    return this.storageService
      .get<boolean>(StorageKeys.NO_CREDIT_CARD_ADDED_SHOWN)
      .pipe(
        switchMap((isNotificationAlreadyShown) => {
          if (isNotificationAlreadyShown === true) {
            return of(null);
          }
          return this.clientService.retrieveClientCard();
        }),
        switchMap((cardDetails) => {
          if (
            cardDetails === null ||
            (!!cardDetails && !!cardDetails.lastFour)
          ) {
            return of(null); // card already added
          }
          return this.modalService.showNoCreditCardAddedModal();
        })
      );
  }

  onWeekChanged(weekIndex: number): void {
    this.selectedWeekIndex = weekIndex;
    this.menuService.weeklyMenus$.pipe(take(1)).subscribe((menus) => {
      const targetWeekNumber =
        weekIndex === WeekIndex.THIS_WEEK
          ? getCurrentWeekNumber()
          : getNextWeekNumber();
      const targetMenu =
        menus?.find((m) => m.weekNumber === targetWeekNumber) || null;
      this.menuService.selectedWeeklyMenu$.next(targetMenu);
      const firstAvailableDay =
        (weekIndex === WeekIndex.THIS_WEEK &&
          targetMenu &&
          this.menuService.isAnyDayAvailableToOrderThisWeek(targetMenu)) ||
        weekIndex === WeekIndex.NEXT_WEEK
          ? this.menuService.findFirstAvailableDayToOrder(weekIndex)
          : getWeekFirstDay();
      this.selectedDay.next(firstAvailableDay);
      this.scrollTrigger$.next();
    });
  }

  onDaySelected(day: WeekDay, productListEl: HTMLElement): void {
    const scrollDelay = 200; // needed to wait until day selector finishes his scroll first
    setTimeout(() => {
      productListEl.scrollIntoView({ behavior: 'smooth' });
    }, scrollDelay);
    this.selectedDay.next(day);
    this.scrollTrigger$.next();
  }

  private initWeeklyMenu(): void {
    const currentWeekNumber = getCurrentWeekNumber();
    const nextWeekNumber = getNextWeekNumber();
    type MenuTuple = [WeeklyMenu, WeekIndex, WeeklyMenu[]];

    this.menuService.weeklyMenus$
      .pipe(
        skipWhile((x) => !x),
        map((menus) => {
          const currentWeekMenu = this.menuService.findMenuForWeek(
            menus!,
            currentWeekNumber
          );
          const nextWeekMenu = this.menuService.findMenuForWeek(
            menus!,
            nextWeekNumber
          );
          if (currentWeekMenu) {
            return [currentWeekMenu, WeekIndex.THIS_WEEK, menus] as MenuTuple;
          }
          if (nextWeekMenu) {
            return [nextWeekMenu, WeekIndex.NEXT_WEEK, menus] as MenuTuple;
          }
          return [null, null, null] as [null, null, null];
        })
      )
      .subscribe(([menu, weekIndex, menus]: MenuTuple | [null, null, null]) => {
        if (!menu) {
          return;
        }
        let targetWeekIndex = weekIndex;
        let targetMenu: WeeklyMenu | null = menu;
        this.menuService.selectedWeeklyMenu$.next(targetMenu);
        const isAnyDayAvailableToOrderThisWeek =
          this.menuService.isAnyDayAvailableToOrderThisWeek(menu);

        this.currentWeekIsNotAvailable = !isAnyDayAvailableToOrderThisWeek;
        const cannotOrderThisWeek =
          targetWeekIndex === WeekIndex.THIS_WEEK &&
          !isAnyDayAvailableToOrderThisWeek;

        const nextWeekMenu =
          menus.find((m) => m.weekNumber === nextWeekNumber) || null;

        if (cannotOrderThisWeek && !!nextWeekMenu) {
          targetWeekIndex = WeekIndex.NEXT_WEEK;
          targetMenu = nextWeekMenu;
        }
        this.selectedWeekIndex = targetWeekIndex;
        this.menuService.selectedWeeklyMenu$.next(targetMenu);

        const firstAvailableDay = targetMenu
          ? this.menuService.findFirstAvailableDayToOrder(targetWeekIndex)
          : getWeekFirstDay();

        if (cannotOrderThisWeek && !nextWeekMenu) {
          this.selectedDay.next('mon');
        } else {
          this.selectedDay.next(firstAvailableDay);
        }

        this.dailyMenu$ = this.selectedDay.pipe(
          switchMap((day) =>
            this.menuService.selectedWeeklyMenu$.pipe(
              map((menu) => menu?.days[day] as DailyMenu)
            )
          )
        );

        this.displayedDays$ = this.menuService.selectedWeeklyMenu$.pipe(
          map((menu) => {
            if (!menu) {
              return [];
            }
            return Object.entries(menu.days)
              .filter(([day, value]) => value !== null)
              .map(([day, value]) => day) as WeekDay[];
          })
        );
      });
  }

  private handleNewProductsPopupPresenting(): void {
    const hasPopupAlreadyShownInCurrentSession =
      this.storageService.getFromSessionStorage<boolean>(
        StorageKeys.NEW_PRODUCTS_SHOWN
      );
    this.clientService.clientLocation$
      .pipe(
        delay(0),
        skipWhile((x) => !x),
        take(1),
        switchMap((location) =>
          this.inStoreProductsService.checkNewProductsByLocation(location.hash)
        ),
        switchMap((products: InStoreProduct[]) => {
          if (products?.length === 0) {
            return of(false);
          }
          return hasPopupAlreadyShownInCurrentSession
            ? of(false)
            : this.modalService
                .showNewProductsPopup(products)
                .pipe(map(() => true));
        })
      )
      .subscribe((isPopupShown: boolean) => {
        if (!isPopupShown) {
          return;
        }
        this.storageService.setToSessionStorage<boolean>(
          StorageKeys.NEW_PRODUCTS_SHOWN,
          true
        );
      });
  }
}
